// @flow
import * as React from 'react';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import { messageFormat as message } from '@clubmed/utils';

import { ICONS } from 'Components/utils/icon/icons';
import { minAge, maxAge } from 'Components/utils/common-validator/CommonValidators';
import FormInputTelDate from 'Components/frameworks/form-input-tel-date/FormInputTelDate.jsx';
import FormValidator from 'Components/frameworks/form-validator-hoc/FormValidator.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { InputNumber } from 'Components/frameworks/form-inputs/input-number/InputNumber.jsx';
import InlineField from 'Components/widgets/booking-form/inline-field/InlineField.jsx';

import { DEFAULT_MIN_AGE, DEFAULT_MAX_AGE } from './participants.constants';

type Props = {
  adultOnly: boolean,
  kids: Array<*>,
  adultsNumber: number,
  setKids: (Array<*>) => void,
  setAdultsNumber: number => void,
  childrenBirthdayDateFormatPlaceholder: string,
  dateFormat: string,
  dayPlaceholder: string,
  monthPlaceholder: string,
  yearPlaceholder: string,
  kidsLabel: string,
  adultsLabel: string,
  kidsAgeLimit: string,
  minAgeInformation: string,
  adultAge: number,
  childAge: number,
  departureDate: string,
  children: Array<*>,
};

export default class Participants extends FormValidator {
  static defaultProps = {
    kids: [],
  };

  getFields(props: Props): Array<FormField> {
    const {
      dateFormat,
      kids,
      adultAge = DEFAULT_MAX_AGE,
      childAge = DEFAULT_MIN_AGE,
      departureDate,
    } = props;

    return kids.map((kid, idx) => {
      const birthdate = this.getKidBirthdate(idx);
      const adultAgeInYear = +adultAge;
      const childAgeInYear = +childAge / 12;

      const params = {
        birthdayFormat: dateFormat,
        onDate: departureDate,
        onDateFormat: 'YYYYMMDD',
      };
      return {
        name: `birthdate.${idx}`,
        value: birthdate,
        validators: [
          maxAge({ ...params, ageRequired: adultAgeInYear }),
          minAge({ ...params, ageRequired: childAgeInYear }),
        ],
      };
    });
  }

  getKidBirthdate(idx: number): string {
    const birthdate = get(this.props.kids[idx], 'birthdate');
    return (
      get(this.state, `fields.${idx}.value`) ||
      (birthdate ? moment(birthdate).format(this.props.dateFormat) : '')
    );
  }

  onAdultsNumberChange = (_: *, value: number) => {
    this.props.setAdultsNumber(value);
  };

  onKidsNumberChange = (_: *, value: number) => {
    const kids = Array(value)
      .fill(undefined)
      .map((kid, i) => {
        if (this.props.kids[i]) return this.props.kids[i];
        return kid;
      });

    this.props.setKids(kids);
  };

  handleKids = (idx: number) => {
    return (value: string) => {
      function updateBirthdate() {
        const kids = this.props.kids.map(
          (kid, index) =>
            idx !== index
              ? kid
              : { ...kid, birthdate: moment(value, this.props.dateFormat).valueOf() },
        );
        this.props.setKids(kids);
      }

      this.validateFields(updateBirthdate);
    };
  };
  renderAdults() {
    return (
      <InputNumber
        className="mx-3"
        dataTestId="ParticipantsAdultsSelect"
        min={1}
        max={7}
        step={1}
        value={this.props.adultsNumber}
        label={this.props.adultsLabel}
        onChange={this.onAdultsNumberChange}
        attendeeType="adults"
      />
    );
  }

  renderChildren() {
    return !this.props.adultOnly ? (
      <InputNumber
        className="mx-3 rtl:sm:ml-10 mt-2 sm:mt-0"
        dataTestId="ParticipantsChildrenSelect"
        min={0}
        max={7}
        step={1}
        value={this.props.kids.length}
        label={this.props.kidsLabel}
        notes={this.props.kidsAgeLimit}
        onChange={this.onKidsNumberChange}
        attendeeType="kids"
      />
    ) : null;
  }

  renderChildrenAgeMessage() {
    const { minAgeInformation, kids } = this.props;

    return minAgeInformation && kids.length > 0 ? (
      <span
        className="BookingForm-minAgeErrorMessage font-sans"
        data-selector="ParticipantsKidsAgeMessage"
      >
        <Icon
          name="smallWarning"
          size="16"
          color="blue"
          className="BookingForm-minAgeErrorMessageIcon"
        />
        {minAgeInformation}
      </span>
    ) : null;
  }

  renderBirthdayInputs(): Array<React.Node> {
    const birthdayLabel = message(get(this.props, 'childrenBirthdayDateFormatPlaceholder', ''));

    return Array.from({ length: this.props.kids.length }).map((child, idx) => {
      const label = birthdayLabel.format({ childIndex: `${idx + 1}` });
      const birthdate = this.getKidBirthdate(idx);
      const { validationState } = get(this.state, `fields.${idx}`, {});

      return (
        <li className="BookingFormChildrenBirthdays-item" key={`BookingForm-ChildBirthday-${idx}`}>
          <div className="BookingFormChildBirthday">
            <FormInputTelDate
              id={`birthday${idx}`}
              label={label}
              dateFormat={this.props.dateFormat}
              dayPlaceholder={this.props.dayPlaceholder}
              monthPlaceholder={this.props.monthPlaceholder}
              yearPlaceholder={this.props.yearPlaceholder}
              onBlur={this.handleKids(idx)}
              onChange={this.updateFieldValue(`birthdate.${idx}`)}
              value={birthdate}
              isRequired
              validationState={validationState}
            />
          </div>
        </li>
      );
    });
  }

  render() {
    return (
      <div className="BookingForm-participantsContainer">
        <div className="BookingForm-adultsAndChildren">
          <InlineField icon={ICONS.USERS}>
            <div className="bg-white flex flex-col sm:flex-row px-4 py-2">
              {this.renderAdults()}
              {this.renderChildren()}
            </div>
          </InlineField>
          {this.renderChildrenAgeMessage()}
        </div>
        {!isEmpty(this.props.kids) && (
          <div className="BookingForm-childrenDOBs">
            <ul className="BookingFormChildrenBirthdays">{this.renderBirthdayInputs()}</ul>
          </div>
        )}
        {this.props.children ? (
          <div className="BookingForm-participantsExtraContent">{this.props.children}</div>
        ) : null}
      </div>
    );
  }
}
